import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ThemeProvider, useTheme, Topbar, PageHeader } from '../shared/DashWidgets';
import Sidebar from '../shared/Sidebar';
import { buildStyles, COLORS } from '../shared/DashTheme';
import api from '../../services/api';

const DEPARTMENTS = [
  "Architecture Engineering","Basic Sciences And Related Studies","Bio-Medical Engineering",
  "Chemical Engineering","City And Regional Planning","Civil Engineering",
  "Computer Systems Engineering","Electrical Engineering","Electronics Engineering",
  "Industrial Engineering","Mechanical Engineering","Mechatronic Engineering",
  "Metallurgy And Materials Engineering","Mining Engineering",
  "Petroleum And Natural Gas Engineering","Software Engineering",
  "Telecommunication Engineering","Textile Engineering"
];

const MAX_UNIVERSITY_ADMINS = 3;

function CreateAdminInner() {
  const { theme } = useTheme();
  const s = buildStyles(theme);
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [formData, setFormData] = useState({ name: '', email: '', department: '', adminType: 'department' });
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [warning, setWarning] = useState('');
  const [loading, setLoading] = useState(false);
  const [shareMethod, setShareMethod] = useState('');
  const [contactInfo, setContactInfo] = useState('');
  const [shareSuccess, setShareSuccess] = useState('');
  const [universityCount, setUniversityCount] = useState(0);
  const [departmentAdmins, setDepartmentAdmins] = useState({});

  useEffect(() => { fetchAdminLimits(); }, []);

  const fetchAdminLimits = async () => {
    try {
      const res = await api.get('/authority/admin-limits');
      setUniversityCount(res.data.university?.current || 0);
      setDepartmentAdmins(res.data.departments?.admins_by_department || {});
    } catch (e) { /* silent */ }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(f => ({ ...f, [name]: value }));
    setError(''); setWarning('');
    if (name === 'department' && formData.adminType === 'department' && value && departmentAdmins[value]) {
      setWarning(`"${value}" already has an admin. Delete the existing admin first.`);
    }
  };

  const handleTypeChange = (type) => {
    setFormData(f => ({ ...f, adminType: type, department: '' }));
    setError(''); setWarning('');
    if (type === 'university' && universityCount >= MAX_UNIVERSITY_ADMINS) {
      setWarning(`Maximum of ${MAX_UNIVERSITY_ADMINS} university admins reached.`);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) { setError('Name is required'); return; }
    if (!formData.email.trim() || !formData.email.includes('@')) { setError('Valid email required'); return; }
    if (formData.adminType === 'department') {
      if (!formData.department) { setError('Select a department'); return; }
      if (departmentAdmins[formData.department]) { setError(`"${formData.department}" already has an admin`); return; }
    }
    if (formData.adminType === 'university' && universityCount >= MAX_UNIVERSITY_ADMINS) {
      setError(`Max ${MAX_UNIVERSITY_ADMINS} university admins reached`); return;
    }
    setLoading(true);
    try {
      const res = await api.post('/authority/create-admin', {
        name: formData.name, email: formData.email,
        department: formData.adminType === 'university' ? 'University' : formData.department,
        adminType: formData.adminType,
      });
      setResult(res.data);
      setFormData({ name: '', email: '', department: '', adminType: 'department' });
      fetchAdminLimits();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to create admin');
    } finally {
      setLoading(false);
    }
  };

  const handleShare = () => {
    if (!shareMethod || !contactInfo.trim()) return;
    const msg = `UniResolve Admin Account\n\nName: ${result.admin?.name}\nEmail: ${result.admin?.email}\nType: ${formData.adminType === 'university' ? 'University Admin' : 'Department Admin'}\nTemp Password: ${result.temp_password}\n\nLogin: ${window.location.origin}/admin-login\n\nChange password after first login.`;
    if (shareMethod === 'email') {
      window.location.href = `mailto:${contactInfo}?subject=UniResolve Admin Account&body=${encodeURIComponent(msg)}`;
    } else {
      window.open(`https://wa.me/${contactInfo.replace(/\D/g,'')}?text=${encodeURIComponent(msg)}`, '_blank');
    }
    setShareSuccess(`Shared via ${shareMethod}`);
    setTimeout(() => setShareSuccess(''), 4000);
  };

  const typeBtn = (type, label, sub) => {
    const active = formData.adminType === type;
    return (
      <div
        onClick={() => handleTypeChange(type)}
        style={{
          flex: 1, padding: '16px', borderRadius: 12, cursor: 'pointer',
          border: active ? `1px solid rgba(255,255,255,0.45)` : '1px solid rgba(255,255,255,0.2)',
          background: active ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.08)',
          transition: 'all 0.2s ease', textAlign: 'center',
        }}
      >
        <div style={{ fontSize: 13, fontWeight: 700, color: '#fff', marginBottom: 4 }}>{label}</div>
        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.65)' }}>{sub}</div>
      </div>
    );
  };

  return (
    <div style={s.page}>
      <div style={s.bgGrid} />
      <div style={s.bgOrb1} />
      <div style={s.bgOrb2} />
      <div style={s.bgOrb3} />
      <div style={s.layout}>
        {sidebarOpen && <Sidebar onToggle={() => setSidebarOpen(o => !o)} />}
        <div style={{ ...s.main, marginLeft: sidebarOpen ? 248 : 0, transition: 'margin-left 0.3s ease' }}>
          <Topbar
            title="Create Admin"
            subtitle="Add a new admin account"
            onToggleSidebar={() => setSidebarOpen(o => !o)}
            actions={
              <button className="ur-btn-hover" style={s.btnSecondary} onClick={() => navigate('/authority/admins')}>
                Manage Admins
              </button>
            }
          />
          <div style={s.content}>
            <div style={{ maxWidth: 580, margin: '0 auto' }}>
              <PageHeader
                icon="➕"
                title="Create Admin"
                subtitle="Add a new admin account"
                stats={[
                  { label: `University`, value: `${universityCount}/${MAX_UNIVERSITY_ADMINS}` },
                  { label: 'Departments', value: `${Object.keys(departmentAdmins).length}/${DEPARTMENTS.length}` },
                ]}
              />
              {/* Limits */}
              <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
                {[
                  [`University: ${universityCount}/${MAX_UNIVERSITY_ADMINS}`, COLORS.purple],
                  [`Departments: ${Object.keys(departmentAdmins).length}/${DEPARTMENTS.length}`, COLORS.blue],
                ].map(([label, color]) => (
                  <div key={label} style={{
                    padding: '8px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600, color: '#fff',
                    background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)',
                    borderLeft: `3px solid ${color}`,
                  }}>{label}</div>
                ))}
              </div>

              <div style={s.card}>
                {error   && <div style={s.alertErr}>{error}</div>}
                {warning && <div style={{ ...s.alertErr, background: 'rgba(245,158,11,0.2)', borderColor: 'rgba(245,158,11,0.35)', color: '#fcd34d' }}>{warning}</div>}

                {result ? (
                  /* Success state */
                  <div>
                    <div style={{ ...s.alertOk, marginBottom: 20 }}>Admin created successfully!</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
                      {[
                        ['Name', result.admin?.name],
                        ['Email', result.admin?.email],
                        ['Type', result.admin?.admin_type === 'university' ? 'University Admin' : 'Department Admin'],
                        ['Department', result.admin?.department],
                      ].filter(([,v]) => v).map(([label, val]) => (
                        <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 14px', borderRadius: 9, background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)' }}>
                          <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)' }}>{label}</span>
                          <span style={{ fontSize: 13, color: '#fff', fontWeight: 600 }}>{val}</span>
                        </div>
                      ))}
                      <div style={{ padding: '14px', borderRadius: 9, background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.3)', textAlign: 'center' }}>
                        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.6 }}>Temporary Password</div>
                        <code style={{ fontSize: 18, fontWeight: 800, color: '#6ee7b7', letterSpacing: 2 }}>{result.temp_password}</code>
                      </div>
                    </div>

                    {/* Share */}
                    <div style={{ ...s.card, background: 'rgba(255,255,255,0.06)', marginBottom: 16 }}>
                      <div style={{ ...s.sectionTitle, marginBottom: 12 }}>Share Login Details</div>
                      <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
                        {['email', 'whatsapp'].map(m => (
                          <button key={m} onClick={() => setShareMethod(m)} style={{
                            flex: 1, padding: '10px', borderRadius: 9, cursor: 'pointer', fontWeight: 600, fontSize: 13,
                            border: shareMethod === m ? '1px solid rgba(255,255,255,0.45)' : '1px solid rgba(255,255,255,0.2)',
                            background: shareMethod === m ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.08)',
                            color: '#fff',
                          }}>
                            {m === 'email' ? 'Email' : 'WhatsApp'}
                          </button>
                        ))}
                      </div>
                      <div style={{ display: 'flex', gap: 10 }}>
                        <input
                          type={shareMethod === 'email' ? 'email' : 'tel'}
                          placeholder={shareMethod === 'email' ? 'Email address' : 'Phone number'}
                          value={contactInfo}
                          onChange={e => setContactInfo(e.target.value)}
                          style={{ ...s.input, flex: 1 }}
                        />
                        <button className="ur-btn-hover" style={{ ...s.btnPrimary, flexShrink: 0 }} onClick={handleShare} disabled={!shareMethod || !contactInfo.trim()}>
                          Send
                        </button>
                      </div>
                      {shareSuccess && <div style={{ ...s.alertOk, marginTop: 10, marginBottom: 0 }}>{shareSuccess}</div>}
                    </div>

                    <button className="ur-btn-hover" style={{ ...s.btnSecondary, width: '100%', textAlign: 'center' }} onClick={() => setResult(null)}>
                      Create Another Admin
                    </button>
                  </div>
                ) : (
                  /* Form */
                  <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                    <div>
                      <label style={s.label}>Admin Type</label>
                      <div style={{ display: 'flex', gap: 12 }}>
                        {typeBtn('department', 'Department Admin', `${Object.keys(departmentAdmins).length}/${DEPARTMENTS.length} slots`)}
                        {typeBtn('university', 'University Admin', `${universityCount}/${MAX_UNIVERSITY_ADMINS} slots`)}
                      </div>
                    </div>

                    <div>
                      <label style={s.label}>Full Name</label>
                      <input name="name" value={formData.name} onChange={handleChange} placeholder="Admin's full name" style={s.input} />
                    </div>

                    <div>
                      <label style={s.label}>Email Address</label>
                      <input name="email" type="email" value={formData.email} onChange={handleChange} placeholder="xyz@admin.edu" style={s.input} />
                    </div>

                    {formData.adminType === 'department' && (
                      <div>
                        <label style={s.label}>Department</label>
                        <select name="department" value={formData.department} onChange={handleChange} style={s.select}>
                          <option value="">Select Department</option>
                          {DEPARTMENTS.map(d => (
                            <option key={d} value={d} disabled={!!departmentAdmins[d]}>
                              {d}{departmentAdmins[d] ? ' (Admin exists)' : ''}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {formData.adminType === 'university' && (
                      <div style={{ padding: '12px 14px', borderRadius: 9, background: 'rgba(139,92,246,0.15)', border: '1px solid rgba(139,92,246,0.3)' }}>
                        <div style={{ fontSize: 13, color: '#fff', fontWeight: 600 }}>University-wide access</div>
                        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.65)', marginTop: 3 }}>This admin can manage all departments</div>
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={loading}
                      className="ur-btn-hover"
                      style={{ ...s.btnPrimary, width: '100%', textAlign: 'center', opacity: loading ? 0.7 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}
                    >
                      {loading ? 'Creating...' : 'Create Admin Account'}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function CreateAdmin() {
  return <ThemeProvider><CreateAdminInner /></ThemeProvider>;
}
